import React, {
  useState, useEffect, useMemo, useRef,
} from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import moment from 'moment-timezone';
import _ from 'lodash';
import { useOutletContext } from 'react-router';
import { DateObject } from 'react-multi-date-picker';
import ReactApexChart from 'react-apexcharts';
import { Skeleton } from '@mui/material';
import { useApiData } from '../../../config/reducer/Modules';
import { APIGetDevices, APIGetV3BatteryAnalysis } from '../../../config/restAPI/FetchAPI';
import ListNavigationTab from '../../../component/listNavigationTab/ListNavigationTab';
import DateRangePicker from '../../../component/dateRangePicker/DateRangePicker';
import Autocomplete from '../../../component/autocomplete/Autocomplete';
import Card from '../../../component/card/Card';
import './BatteryDashboard.css';

const createSkeletonKeys = (prefix, count) => (
  Array.from({ length: count }, (unused, index) => `${prefix}-${index}`)
);

const VcmKpiCardSkeleton = () => (
  <div
    className="styleCard"
    style={{
      width: '100%',
      height: '190px',
      boxSizing: 'border-box',
    }}
  >
    <div className="styleCardContent">
      <Skeleton variant="text" width="55%" height={24} />
      <div className="styleDescription">
        <div className="styleCardBody">
          <Skeleton variant="text" width={120} height={56} />
          <Skeleton variant="text" width="65%" height={20} />
        </div>
      </div>
    </div>
  </div>
);

const VcmChartCardSkeleton = () => (
  <div className="battery-dashboard-chart-card">
    <div className="battery-dashboard-chart-header">
      <Skeleton variant="text" width="40%" height={28} />
    </div>
    <div className="battery-dashboard-chart">
      <Skeleton variant="rectangular" height={350} sx={{ borderRadius: '8px' }} />
    </div>
  </div>
);

const VcmTableCardSkeleton = ({ columnCount = 5, rowCount = 4 }) => (
  <div className="battery-dashboard-table-card">
    <Skeleton variant="text" width="55%" height={24} sx={{ mb: 1.5 }} />
    <div className="battery-dashboard-table-container">
      <table className="battery-dashboard-table">
        <thead>
          <tr>
            {createSkeletonKeys('vcm-table-skeleton-th', columnCount).map((columnKey) => (
              <th key={columnKey} aria-label="Loading column">
                <Skeleton variant="text" width="80%" height={18} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {createSkeletonKeys('vcm-table-skeleton-row', rowCount).map((rowKey) => (
            <tr key={rowKey}>
              {createSkeletonKeys('column', columnCount).map((columnKey) => (
                <td key={`${rowKey}-${columnKey}`} aria-label="Loading data">
                  <Skeleton variant="text" width="90%" height={18} />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div className="battery-dashboard-table-pagination">
        <Skeleton variant="text" width={40} height={20} />
      </div>
    </div>
  </div>
);

const BatteryDashboard = () => {
  const { state } = useLocation();
  const token = Cookies.get('jwtToken');
  const agencyID = Cookies.get('agencyUUID');
  const userTimezone = Cookies.get('timezone');
  const { apiResponse } = useApiData();
  const [loadAsset, setLoadAsset] = useState(true); // for getSearchAsset loader
  const [navigationClick, setNavigationClick, , setNavigationState] = useOutletContext();
  const [listAsset, setListAsset] = useState([]); // this is for list search asset dropdown
  const [assetSelectionList, setAssetSelectionList] = useState({}); // this for asset selection state
  const [inputSelectonText, setInputSelectonText] = useState('');
  const [date, setDate] = useState([]);
  const [inputValue, setInputValue] = useState('Select Dates');
  const [searchTimeout, setSearchTimeout] = useState(null);// buffer when do search
  const [selectedOption, setSelectedOption] = useState(null);
  const uuid = state?.uuid || '';
  const [dateRange, setDateRange] = useState('');
  const [dropDownLoading, setDropDownLoading] = useState(false);
  const [minDateRange, setMinDateRange] = useState(new DateObject().subtract(1, 'year'));
  const [maxDateRange, setMaxDateRange] = useState(new Date());
  const [loadBattery, setLoadBattery] = useState(true);
  const batteryRequestRef = useRef(0);
  const lastBatteryRequestKey = useRef('');
  const requestAssetsKey = useMemo(
    () => listAsset.map((asset) => asset?.id).filter(Boolean).join('|'),
    [listAsset],
  );
  const [crashPage, setCrashPage] = useState(1);
  const [batteryPage, setBatteryPage] = useState(1);
  const [unplugPage, setUnplugPage] = useState(1);
  const [signalPage, setSignalPage] = useState(1);
  const [crashSort, setCrashSort] = useState({ key: 'dateTime', direction: 'desc' });
  const [batterySort, setBatterySort] = useState({ key: 'dateTime', direction: 'desc' });
  const [unplugSort, setUnplugSort] = useState({ key: 'dateTime', direction: 'desc' });
  const [signalSort, setSignalSort] = useState({ key: 'dateTime', direction: 'desc' });

  // VCM Dashboard Data
  const [vcmData, setVcmData] = useState({
    incidents: {
      crashTowing: { value: 0, change: 0, isPositive: true },
      batterySystem: { value: 0, change: 0, isPositive: true },
      unplugSecurity: { value: 0, change: 0, isPositive: true },
      signalDevice: { value: 0, change: 0, isPositive: true },
    },
    batteryVoltageChart: { data24V: [], data12V: [] },
    crashTowingTable: [],
    batteryDiagnosticTable: [],
    unplugSecurityTable: [],
    signalDeviceTable: [],
  });

const assetPerformanceNavigationList = [
  {
    text: 'Executive Summary and Fleet Health ', value: 1, path: '/analytics/fleetDashboard', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.analytics?.fleetDashboard,
},
  {
      text: 'Fuel Management System', value: 2, path: '/analytics/analyticsDashboard', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.analytics?.analyticsDashboard,
  },
  {
      text: 'VCM & Maintenance Management', value: 3, path: '/analytics/batteryDashboard', enabled: (apiResponse?.role === 'Agency Administrator' || apiResponse?.role === 'Agency Owner') ? true : apiResponse?.module?.analytics?.batteryDashboard,
  },
];

const APICalledSearchAssetNGroup = async (inputText) => {
  if (!token || !agencyID) {
      setListAsset([]);
      setLoadAsset(false);
      return;
  }

  const params = {
      pageNumber: 1,
      totalPerPage: 100,
      searchKey: inputText,
  };
  try {
      const result = await APIGetDevices(setLoadAsset, token, agencyID, params);
      if (result.data.status === 200) {
          const { data } = result.data;
          const asset = data && data.length > 0 ? data.map((x) => ({
              uuid: x.oid,
              id: x.assetName,
              name: x.deviceSerial ? x.deviceSerial : '',
              pairId: x.oid,
              pairedAssets: [],
              fuelSource: x.fuelSource,
              mileageSource: x.mileageSource,
          })) : [];
          const sortCompare = asset.length > 0 ? _.sortBy(asset, 'name') : [];
          setListAsset(sortCompare);
          if (state?.uuid) {
              setSelectedOption(asset.findIndex((e) => e.uuid === uuid));
              setAssetSelectionList(asset.find((e) => e.uuid === uuid));
          }
          setLoadAsset(false);
      } else {
          setListAsset([]);
          setLoadAsset(false);
      }
  } catch (err) {
      setLoadAsset(false);
  }
};

const formatPickerDate = (value) => moment(new Date(value)).format('MMM D, YYYY');

    // this useEffect for call the searchAsset API called. Interval for 0.5 sec
    useEffect(() => {
      if (searchTimeout) {
          clearTimeout(searchTimeout); // this to clear time out
      }
      setSearchTimeout( // to buffer the search by 0.5 second
          setTimeout(() => {
          // APICalledSearchAssetNGroup(); // call the api
          APICalledSearchAssetNGroup(inputSelectonText);
      }, 500),
      );
      return () => clearTimeout(searchTimeout); // this also to clear time out
  }, [inputSelectonText]);

  // this useEffect for onPageLoad
  useEffect(() => {
      if (token) {
          APICalledSearchAssetNGroup();
      } else {
          setLoadAsset(false);
      }
      // Default to last 7 days on first load (same as Fleet/Fuel dashboards)
      const defaultStart = new DateObject().subtract(6, 'days');
      const defaultEnd = new DateObject();
      setDate([defaultStart, defaultEnd]);
      setInputValue(`${formatPickerDate(defaultStart)} - ${formatPickerDate(defaultEnd)}`);
      window.history.replaceState({}, document.title);
      setNavigationClick(3);
      setNavigationState('analytics');
  }, []);

  useEffect(() => {
      APICalledSearchAssetNGroup('');
      setDropDownLoading(false);
  }, [dropDownLoading]);

  const toNumber = (value) => {
    const num = Number(value);
    return Number.isFinite(num) ? num : 0;
  };

  const getFirstArray = (payload, keys) => {
    for (let i = 0; i < keys.length; i += 1) {
      const candidate = payload?.[keys[i]];
      if (Array.isArray(candidate)) return candidate;
    }
    return [];
  };

  const toRawDateValue = (value) => {
    if (value && typeof value === 'object') {
      return value?.value || value?.date || value?.timestamp || value?.datetime || null;
    }
    return value;
  };

  const toDisplayDate = (value, includeTime = false) => {
    const rawValue = toRawDateValue(value);
    if (!rawValue) return '-';
    const m = moment(rawValue);
    if (!m.isValid()) return String(rawValue);
    return includeTime ? m.format('MMM DD, YYYY, h:mm A') : m.format('MMM DD, YYYY');
  };

  const getRowDateTime = (item) => (
    item?.date
    || item?.date_time
    || item?.dateTime
    || item?.timestamp
    || item?.eventTime
    || item?.event_time
    || item?.analysis_date
    || item?.analysis_time
    || item?.analysisDate
    || item?.created_at
    || item?.createdAt
    || item?.datetime?.value
    || item?.hour?.value
    || item?.bqTimestampHour?.value
  );

  const toChartTimestamp = (item) => {
    const rawTime = item?.x
      || item?.timestamp
      || item?.date
      || item?.time
      || item?.analysis_date
      || item?.analysis_time
      || item?.analysisDate
      || item?.hour?.value
      || item?.bqTimestampHour?.value
      || item?.datetime?.value;
    const ts = moment(rawTime).valueOf();
    return Number.isFinite(ts) ? ts : null;
  };

  const mapBatteryPayload = (data = {}, prefix = 'a0') => {
    const voltageRows = getFirstArray(data, [
      'batteryVoltageChart',
      'battery_voltage_chart',
      'batteryVoltageTrend',
      'battery_voltage_trend',
      'voltage_chart',
      'battery_voltage',
    ]);

    const data24V = voltageRows.map((item) => ({
      x: toChartTimestamp(item),
      y: toNumber(
        item?.v24
        || item?.battery24V
        || item?.voltage24V
        || item?.value24
        || item?.avg_voltage_24v
        || item?.avgVoltage24v
        || item?.avg_voltage_24
        || item?.avgVoltage24
        || item?.voltage_24
        || item?.voltage24
        || (((item?.battery_type || item?.batteryType || '').toString().includes('24'))
          ? item?.voltage || item?.value
          : null),
      ),
    })).filter((item) => item.x !== null);

    const data12V = voltageRows.map((item) => ({
      x: toChartTimestamp(item),
      y: toNumber(
        item?.v12
        || item?.battery12V
        || item?.voltage12V
        || item?.value12
        || item?.avg_voltage_12v
        || item?.avgVoltage12v
        || item?.avg_voltage_12
        || item?.avgVoltage12
        || item?.voltage_12
        || item?.voltage12
        || (((item?.battery_type || item?.batteryType || '').toString().includes('12'))
          ? item?.voltage || item?.value
          : null),
      ),
    })).filter((item) => item.x !== null);

    const crashTowingRows = getFirstArray(data, [
      'crashTowingTable',
      'crash_towing_table',
      'crashTowingIncidents',
      'crash_towing_incidents',
      'crash_rows',
    ]).map((item, idx) => ({
      id: `${prefix}-ct-${idx}`,
      assetName: item?.assetName || item?.asset_name || item?.asset_id || item?.assetId || '-',
      dateTime: toDisplayDate(getRowDateTime(item), true),
      alertType: item?.alertType || item?.alert_type || item?.type || '-',
      status: item?.status || item?.state || item?.reading || '-',
    }));

    const batteryDiagnosticRows = getFirstArray(data, [
      'batteryDiagnosticTable',
      'battery_diagnostic_table',
      'batterySystemDiagnostic',
      'battery_system_diagnostic',
      'battery_diagnostic',
      'battery_diagnostic_rows',
    ]).map((item, idx) => ({
      id: `${prefix}-bd-${idx}`,
      assetName: item?.assetName || item?.asset_name || item?.asset_id || item?.assetId || '-',
      dateTime: toDisplayDate(getRowDateTime(item), true),
      alertType: item?.alertType || item?.alert_type || item?.type || '-',
      vHrDrop: item?.vHrDrop
        || item?.vhr_drop
        || item?.v_hr_drop
        || item?.vhrDrop
        || item?.voltageDrop
        || item?.voltage_drop
        || item?.voltage
        || item?.value
        || '-',
      message: item?.message
        || item?.msg
        || item?.description
        || item?.details
        || item?.remark
        || item?.reason
        || item?.status
        || item?.state
        || '-',
    }));

    const unplugSecurityRows = getFirstArray(data, [
      'unplugSecurityTable',
      'unplug_security_table',
      'unplugSecurity',
      'unplug_security',
      'unplug_security_rows',
    ]).map((item, idx) => ({
      id: `${prefix}-us-${idx}`,
      assetName: item?.assetName || item?.asset_name || item?.asset_id || item?.assetId || '-',
      dateTime: toDisplayDate(getRowDateTime(item), true),
      status: item?.status || item?.state || '-',
    }));

    const signalDeviceRows = getFirstArray(data, [
      'signalDeviceTable',
      'signal_device_table',
      'signalDeviceIntegrity',
      'signal_device_integrity',
      'signal_device_integrity_rows',
    ]).map((item, idx) => ({
      id: `${prefix}-sd-${idx}`,
      assetName: item?.assetName || item?.asset_name || item?.asset_id || item?.assetId || '-',
      dateTime: toDisplayDate(getRowDateTime(item), true),
      alertType: item?.alertType || item?.alert_type || item?.type || '-',
      message: item?.message
        || item?.msg
        || item?.description
        || item?.details
        || item?.remark
        || item?.reason
        || item?.status
        || item?.state
        || '-',
    }));

    const crashChange = toNumber(data?.crashTowingChange || data?.crash_towing_change || data?.difference_in_crash);
    const batteryChange = toNumber(data?.batterySystemChange || data?.battery_system_change || data?.difference_in_battery_diagnostic);
    const unplugChange = toNumber(data?.unplugSecurityChange || data?.unplug_security_change || data?.difference_in_unplug_security);
    const signalChange = toNumber(data?.signalDeviceChange || data?.signal_device_change || data?.difference_in_signal_device_integrity);

    return {
      incidents: {
        crashTowing: {
          value: toNumber(
            data?.crashTowingIncidents
            || data?.crash_towing_incidents
            || data?.crashTowing
            || data?.crash_towing
            || data?.total_crash,
          ),
          change: crashChange,
          isPositive: crashChange >= 0,
        },
        batterySystem: {
          value: toNumber(
            data?.batterySystemDiagnostic
            || data?.battery_system_diagnostic
            || data?.batterySystem
            || data?.battery_system
            || data?.total_battery_diagnostic,
          ),
          change: batteryChange,
          isPositive: batteryChange >= 0,
        },
        unplugSecurity: {
          value: toNumber(
            data?.unplugSecurityIncidents
            || data?.unplug_security_incidents
            || data?.unplugSecurity
            || data?.unplug_security
            || data?.total_unplug_security,
          ),
          change: unplugChange,
          isPositive: unplugChange >= 0,
        },
        signalDevice: {
          value: toNumber(
            data?.signalDeviceIntegrity
            || data?.signal_device_integrity
            || data?.signalDevice
            || data?.signal_device
            || data?.total_signal_device_integrity,
          ),
          change: signalChange,
          isPositive: signalChange >= 0,
        },
      },
      batteryVoltageChart: { data24V, data12V },
      crashTowingTable: crashTowingRows,
      batteryDiagnosticTable: batteryDiagnosticRows,
      unplugSecurityTable: unplugSecurityRows,
      signalDeviceTable: signalDeviceRows,
    };
  };

  const fetchBatteryAnalysis = async (fromTime, toTime, selectedAssetName) => {
    if (!token || !agencyID) return;
    batteryRequestRef.current += 1;
    const currentRequestId = batteryRequestRef.current;
    setLoadBattery(true);
    try {
      const allAssetNames = listAsset.map((asset) => asset?.id).filter((name) => Boolean(name));
      const assetNamesParam = selectedAssetName ? [selectedAssetName] : allAssetNames;
      const parsePayloadsFromResult = (apiResult) => {
        const isSuccess = apiResult?.data?.status === 200 || apiResult?.status === 200;
        if (!isSuccess) return [];
        const responseData = apiResult?.data?.data ?? apiResult?.data;
        if (Array.isArray(responseData)) return responseData;
        if (responseData && typeof responseData === 'object') return [responseData];
        return [];
      };

      if (assetNamesParam.length === 0) {
        if (currentRequestId !== batteryRequestRef.current) return;
        setLoadBattery(false);
        return;
      }

      const result = await APIGetV3BatteryAnalysis(
        setLoadBattery,
        token,
        agencyID,
        fromTime,
        toTime,
        assetNamesParam,
      );

      let normalizedPayloads = parsePayloadsFromResult(result);

      // Retry selected asset as plain string if backend rejects single-item array.
      if (normalizedPayloads.length === 0 && selectedAssetName) {
        const singleAssetResult = await APIGetV3BatteryAnalysis(
          setLoadBattery,
          token,
          agencyID,
          fromTime,
          toTime,
          selectedAssetName,
        );
        normalizedPayloads = parsePayloadsFromResult(singleAssetResult);
      } else if (normalizedPayloads.length === 0 && !selectedAssetName) {
        // Fallback if backend rejects bulk format for assetNames.
        const FALLBACK_BATCH_SIZE = 10;
        const results = [];
        for (let index = 0; index < allAssetNames.length; index += FALLBACK_BATCH_SIZE) {
          const batch = allAssetNames.slice(index, index + FALLBACK_BATCH_SIZE);
          // Run each batch concurrently, but process batches sequentially to avoid flooding.
          // eslint-disable-next-line no-await-in-loop
          const batchResults = await Promise.all(
            batch.map((assetName) => APIGetV3BatteryAnalysis(
              setLoadBattery,
              token,
              agencyID,
              fromTime,
              toTime,
              assetName,
            )),
          );
          results.push(...batchResults);
        }
        normalizedPayloads = results
          .filter((r) => r?.data?.status === 200 || r?.status === 200)
          .map((r) => {
            const responseData = r?.data?.data ?? r?.data;
            return Array.isArray(responseData) ? (responseData[0] || {}) : (responseData || {});
          });
      }

      const mapped = normalizedPayloads.map((payload, idx) => mapBatteryPayload(payload, `a${idx}`));

      if (mapped.length === 0) {
        if (currentRequestId !== batteryRequestRef.current) return;
        setVcmData((prev) => ({
          ...prev,
          incidents: {
            crashTowing: { value: 0, change: 0, isPositive: true },
            batterySystem: { value: 0, change: 0, isPositive: true },
            unplugSecurity: { value: 0, change: 0, isPositive: true },
            signalDevice: { value: 0, change: 0, isPositive: true },
          },
          batteryVoltageChart: { data24V: [], data12V: [] },
          crashTowingTable: [],
          batteryDiagnosticTable: [],
          unplugSecurityTable: [],
          signalDeviceTable: [],
        }));
        setLoadBattery(false);
      } else {
        const avg = (arr, selector) => (
          arr.reduce((sum, item) => sum + selector(item), 0) / arr.length
        );

        if (currentRequestId !== batteryRequestRef.current) return;
        setVcmData({
          incidents: {
            crashTowing: {
              value: mapped.reduce((sum, x) => sum + x.incidents.crashTowing.value, 0),
              change: avg(mapped, (x) => x.incidents.crashTowing.change),
              isPositive: avg(mapped, (x) => x.incidents.crashTowing.change) >= 0,
            },
            batterySystem: {
              value: mapped.reduce((sum, x) => sum + x.incidents.batterySystem.value, 0),
              change: avg(mapped, (x) => x.incidents.batterySystem.change),
              isPositive: avg(mapped, (x) => x.incidents.batterySystem.change) >= 0,
            },
            unplugSecurity: {
              value: mapped.reduce((sum, x) => sum + x.incidents.unplugSecurity.value, 0),
              change: avg(mapped, (x) => x.incidents.unplugSecurity.change),
              isPositive: avg(mapped, (x) => x.incidents.unplugSecurity.change) >= 0,
            },
            signalDevice: {
              value: mapped.reduce((sum, x) => sum + x.incidents.signalDevice.value, 0),
              change: avg(mapped, (x) => x.incidents.signalDevice.change),
              isPositive: avg(mapped, (x) => x.incidents.signalDevice.change) >= 0,
            },
          },
          batteryVoltageChart: {
            data24V: mapped.flatMap((x) => x.batteryVoltageChart.data24V).sort((a, b) => a.x - b.x),
            data12V: mapped.flatMap((x) => x.batteryVoltageChart.data12V).sort((a, b) => a.x - b.x),
          },
          crashTowingTable: mapped.flatMap((x) => x.crashTowingTable),
          batteryDiagnosticTable: mapped.flatMap((x) => x.batteryDiagnosticTable),
          unplugSecurityTable: mapped.flatMap((x) => x.unplugSecurityTable),
          signalDeviceTable: mapped.flatMap((x) => x.signalDeviceTable),
        });
        setLoadBattery(false);
      }
    } catch (error) {
      if (currentRequestId !== batteryRequestRef.current) return;
      setVcmData((prev) => ({
        ...prev,
        batteryVoltageChart: { data24V: [], data12V: [] },
        crashTowingTable: [],
        batteryDiagnosticTable: [],
        unplugSecurityTable: [],
        signalDeviceTable: [],
      }));
      setLoadBattery(false);
    }
  };

  const rowsPerPage = 4;
  const getPaginatedData = (rows, currentPage) => {
    const totalRows = rows.length;
    const totalPages = Math.max(1, Math.ceil(totalRows / rowsPerPage));
    const safePage = Math.min(Math.max(currentPage, 1), totalPages);
    const startIdx = (safePage - 1) * rowsPerPage;
    const endIdx = Math.min(startIdx + rowsPerPage, totalRows);
    return {
      rows: rows.slice(startIdx, endIdx),
      page: safePage,
      totalPages,
      startDisplay: totalRows === 0 ? 0 : startIdx + 1,
    };
  };

  useEffect(() => {
    if (!token || !agencyID || date.length < 2 || loadAsset) return;
    const requestTimezone = userTimezone || moment.tz.guess();
    const fromTime = moment(date[0]?.toDate?.() || date[0]).tz(requestTimezone).startOf('day').format('YYYY-MM-DD[T]HH:mm:ss');
    const toTime = moment(date[1]?.toDate?.() || date[1]).tz(requestTimezone).endOf('day').format('YYYY-MM-DD[T]HH:mm:ss');
    const assetName = assetSelectionList?.id || null;
    // Asset searches toggle loadAsset even when their resolved result is unchanged.
    const requestKey = `${fromTime}|${toTime}|${assetName || ''}|${requestAssetsKey}`;
    if (lastBatteryRequestKey.current === requestKey) return;
    lastBatteryRequestKey.current = requestKey;
    fetchBatteryAnalysis(fromTime, toTime, assetName);
  }, [date, assetSelectionList?.id, requestAssetsKey, listAsset.length, loadAsset, agencyID]);

  // If asset search finished with no devices, stop showing the skeleton
  useEffect(() => {
    if (!loadAsset && listAsset.length === 0) {
      setLoadBattery(false);
    }
  }, [loadAsset, listAsset.length]);

  useEffect(() => {
    setCrashPage(1);
    setBatteryPage(1);
    setUnplugPage(1);
    setSignalPage(1);
  }, [
    vcmData.crashTowingTable.length,
    vcmData.batteryDiagnosticTable.length,
    vcmData.unplugSecurityTable.length,
    vcmData.signalDeviceTable.length,
  ]);

  const sortTable = (rows, sortState) => {
    const { key, direction } = sortState;
    const factor = direction === 'asc' ? 1 : -1;
    const copy = [...rows];
    copy.sort((a, b) => {
      const aVal = a[key] ?? '';
      const bVal = b[key] ?? '';
      if (key.toLowerCase().includes('date')) {
        const aTime = moment(aVal, 'MMM DD, YYYY, h:mm A', true).valueOf() || moment(aVal).valueOf() || 0;
        const bTime = moment(bVal, 'MMM DD, YYYY, h:mm A', true).valueOf() || moment(bVal).valueOf() || 0;
        if (aTime === bTime) return 0;
        return aTime > bTime ? factor : -factor;
      }
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        if (aVal === bVal) return 0;
        return aVal > bVal ? factor : -factor;
      }
      const aStr = String(aVal);
      const bStr = String(bVal);
      if (aStr === bStr) return 0;
      return aStr > bStr ? factor : -factor;
    });
    return copy;
  };

  const crashSorted = useMemo(
    () => sortTable(vcmData.crashTowingTable, crashSort),
    [vcmData.crashTowingTable, crashSort],
  );
  const batterySorted = useMemo(
    () => sortTable(vcmData.batteryDiagnosticTable, batterySort),
    [vcmData.batteryDiagnosticTable, batterySort],
  );
  const unplugSorted = useMemo(
    () => sortTable(vcmData.unplugSecurityTable, unplugSort),
    [vcmData.unplugSecurityTable, unplugSort],
  );
  const signalSorted = useMemo(
    () => sortTable(vcmData.signalDeviceTable, signalSort),
    [vcmData.signalDeviceTable, signalSort],
  );

  const crashPagination = getPaginatedData(crashSorted, crashPage);
  const batteryPagination = getPaginatedData(batterySorted, batteryPage);
  const unplugPagination = getPaginatedData(unplugSorted, unplugPage);
  const signalPagination = getPaginatedData(signalSorted, signalPage);

  const toggleSort = (current, key) => {
    if (current.key === key) {
      return { key, direction: current.direction === 'asc' ? 'desc' : 'asc' };
    }
    return { key, direction: 'asc' };
  };

  const crashSortArrowChar = (columnKey) => {
    if (crashSort.key !== columnKey) return '↕';
    return crashSort.direction === 'desc' ? '↓' : '↑';
  };
  const batterySortArrowChar = (columnKey) => {
    if (batterySort.key !== columnKey) return '↕';
    return batterySort.direction === 'desc' ? '↓' : '↑';
  };
  const unplugSortArrowChar = (columnKey) => {
    if (unplugSort.key !== columnKey) return '↕';
    return unplugSort.direction === 'desc' ? '↓' : '↑';
  };
  const signalSortArrowChar = (columnKey) => {
    if (signalSort.key !== columnKey) return '↕';
    return signalSort.direction === 'desc' ? '↓' : '↑';
  };

  const sortArrowClass = (activeKey, columnKey) => (
    activeKey === columnKey
      ? 'battery-dashboard-sort-arrow battery-dashboard-sort-arrow-active'
      : 'battery-dashboard-sort-arrow battery-dashboard-sort-arrow-idle'
  );

  const comparisonDays = useMemo(() => {
    if (date.length < 2) return 7;
    const startDate = moment(date[0]?.toDate?.() || date[0]).startOf('day');
    const endDate = moment(date[1]?.toDate?.() || date[1]).startOf('day');
    if (!startDate.isValid() || !endDate.isValid()) return 7;
    return Math.abs(endDate.diff(startDate, 'days')) + 1;
  }, [date]);

  // Chart options for Battery Voltage Trend
  const batteryVoltageChartOptions = {
    chart: {
      id: 'battery-voltage-chart',
      type: 'line',
      toolbar: { show: false },
      fontFamily: 'Inter',
    },
    stroke: {
      curve: 'smooth',
      width: 2,
    },
    xaxis: {
      type: 'datetime',
      labels: {
        formatter: (value) => moment(value).format('MMM DD, h:mm A'),
      },
    },
    yaxis: {
      title: {
        text: 'Voltage (V)',
      },
      min: 0,
      max: 30,
      labels: {
        formatter: (value) => value.toFixed(2),
      },
    },
    tooltip: {
      x: {
        formatter: (value) => moment(value).format('MMM DD, YYYY h:mm A'),
      },
    },
    colors: ['#3B82F6', '#F59E0B'],
    legend: {
      show: true,
      position: 'top',
    },
  };

  return (
    // <div className="analytics-dashboard-container">
    <div className="battery-dashboard-container">
      <ListNavigationTab list={assetPerformanceNavigationList} navigtionClick={navigationClick} setNavigtionClick={setNavigationClick} />

      {/* Main Title with Filters */}
      <div className="battery-dashboard-header">
        <div className="battery-dashboard-header-left">
          <h1 className="battery-dashboard-main-title">VCM & Maintenance Management</h1>
          <p className="battery-dashboard-description">
          This dashboard provides an executive overview of fleet health, focusing on Vehicle Condition Monitoring (VCM) and maintenance management to ensure optimal performance and efficient resource utilization.
          </p>
        </div>
        <div className="battery-analytics-filters">
          <div className="battery-analytics-filter-box">
            <DateRangePicker
              date={date}
              setDate={setDate}
              inputValue={inputValue}
              setInputValue={setInputValue}
              minDateRange={minDateRange}
              setMinDateRange={setMinDateRange}
              maxDateRange={maxDateRange}
              setMaxDateRange={setMaxDateRange}
              setApplyDatePicker={() => {}}
              dateRange={dateRange}
              setDateRange={setDateRange}
            />
          </div>
          <div className="battery-analytics-filter-box">
            <Autocomplete
              component="BatteryDashboard"
              autocompleteComponent="AssetSearch"
              list={listAsset}
              setSelectionList={setAssetSelectionList}
              selectionList={assetSelectionList}
              text="Select asset / device"
              isImage
              isAgencySelector={false}
              inputValue={inputSelectonText}
              stateInputValue={setInputSelectonText}
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              dropDownLoading={dropDownLoading}
              setDropDownLoading={setDropDownLoading}
              loadAsset={loadAsset}
            />
          </div>
        </div>
      </div>

      {loadBattery ? (
        <>
          <div className="battery-dashboard-kpi-section">
            <VcmKpiCardSkeleton />
            <VcmKpiCardSkeleton />
            <VcmKpiCardSkeleton />
            <VcmKpiCardSkeleton />
          </div>
          <div className="battery-dashboard-chart-section">
            <VcmChartCardSkeleton />
          </div>
          <div className="battery-dashboard-tables-section">
            <Skeleton variant="text" width="35%" height={32} />
            <div className="battery-dashboard-tables-container">
              <VcmTableCardSkeleton columnCount={5} />
              <VcmTableCardSkeleton columnCount={6} />
              <VcmTableCardSkeleton columnCount={4} />
              <VcmTableCardSkeleton columnCount={5} />
            </div>
          </div>
        </>
      ) : (
        <>
      {/* VCM Incident Summary Cards - Top */}
      <div className="battery-dashboard-kpi-section">
        <Card
          title="Crash/Towing Incidents"
          body={vcmData.incidents.crashTowing.value.toLocaleString()}
          checkArrow={vcmData.incidents.crashTowing.isPositive}
          width="100%"
        />
        <Card
          title="Battery System Diagnostic"
          body={vcmData.incidents.batterySystem.value.toLocaleString()}
          checkArrow={vcmData.incidents.batterySystem.isPositive}
          width="100%"
        />
        <Card
          title="Unplug & Security"
          body={vcmData.incidents.unplugSecurity.value.toLocaleString()}
          checkArrow={vcmData.incidents.unplugSecurity.isPositive}
          width="100%"
        />
        <Card
          title="Signal & Device Integrity"
          body={vcmData.incidents.signalDevice.value.toLocaleString()}
          checkArrow={vcmData.incidents.signalDevice.isPositive}
          width="100%"
        />
      </div>

      {/* Battery Voltage Trend Chart - Below */}
      <div className="battery-dashboard-chart-section">
        <div className="battery-dashboard-chart-card">
          <div className="battery-dashboard-chart-header">
            <h3 className="battery-dashboard-chart-title">Battery Voltage Trend</h3>
          </div>
          <div className="battery-dashboard-chart">
            <ReactApexChart
              options={batteryVoltageChartOptions}
              series={[
                { name: '24 V Battery', data: vcmData.batteryVoltageChart.data24V || [] },
                { name: '12 V Battery', data: vcmData.batteryVoltageChart.data12V || [] },
              ]}
              type="line"
              height={350}
            />
          </div>
        </div>
      </div>

      {/* Integrity Timeline Chart */}
      {/* <div className="battery-dashboard-integrity-section"> */}
        {/* <div className="battery-dashboard-integrity-label">Integrity</div>
        <div className="battery-dashboard-integrity-chart">
          <ReactApexChart
            options={integrityTimelineChartOptions}
            series={[{ name: 'Integrity', data: vcmData.integrityTimelineChart || [] }]}
            type="line"
            height={100}
          />
        </div> */}
      {/* </div> */}

      {/* Bottom Section: Detailed VCM Diagnostic Log Tables */}
      <div className="battery-dashboard-tables-section">
        <h2 className="battery-dashboard-tables-title">Detailed VCM Diagnostic Log</h2>
        <div className="battery-dashboard-tables-container">
          {/* Top-Left Table: Crash/Towing Incidents */}
          <div className="battery-dashboard-table-card">
            <h3 className="battery-dashboard-table-card-title battery-dashboard-table-header-red">Crash/Towing Incidents</h3>
            <div className="battery-dashboard-table-container">
              <table className="battery-dashboard-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setCrashSort((prev) => toggleSort(prev, 'assetName'))}
                      >
                        Asset Name
                        <span className={sortArrowClass(crashSort.key, 'assetName')}>
                          {crashSortArrowChar('assetName')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setCrashSort((prev) => toggleSort(prev, 'dateTime'))}
                      >
                        Date &amp; Time
                        <span className={sortArrowClass(crashSort.key, 'dateTime')}>
                          {crashSortArrowChar('dateTime')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setCrashSort((prev) => toggleSort(prev, 'alertType'))}
                      >
                        Alert Type
                        <span className={sortArrowClass(crashSort.key, 'alertType')}>
                          {crashSortArrowChar('alertType')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setCrashSort((prev) => toggleSort(prev, 'status'))}
                      >
                        Status
                        <span className={sortArrowClass(crashSort.key, 'status')}>
                          {crashSortArrowChar('status')}
                        </span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {crashPagination.rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{crashPagination.startDisplay + index}</td>
                      <td className="battery-dashboard-cell-asset-name">{row.assetName}</td>
                      <td>{row.dateTime}</td>
                      <td className="battery-dashboard-cell-alert-type">{row.alertType}</td>
                      <td className="battery-dashboard-cell-wrap">{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="battery-dashboard-table-pagination">
                <span>{`${crashPagination.page}/${crashPagination.totalPages}`}</span>
                <div className="battery-dashboard-pagination-arrows">
                  <button
                    type="button"
                    onClick={() => setCrashPage((prev) => Math.max(prev - 1, 1))}
                    disabled={crashPagination.page <= 1}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setCrashPage((prev) => Math.min(prev + 1, crashPagination.totalPages))}
                    disabled={crashPagination.page >= crashPagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Top-Right Table: Battery System Diagnostic */}
          <div className="battery-dashboard-table-card">
            <h3 className="battery-dashboard-table-card-title battery-dashboard-table-header-orange">Battery System Diagnostic</h3>
            <div className="battery-dashboard-table-container">
              <table className="battery-dashboard-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setBatterySort((prev) => toggleSort(prev, 'assetName'))}
                      >
                        Asset Name
                        <span className={sortArrowClass(batterySort.key, 'assetName')}>
                          {batterySortArrowChar('assetName')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setBatterySort((prev) => toggleSort(prev, 'dateTime'))}
                      >
                        Date &amp; Time
                        <span className={sortArrowClass(batterySort.key, 'dateTime')}>
                          {batterySortArrowChar('dateTime')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setBatterySort((prev) => toggleSort(prev, 'alertType'))}
                      >
                        Alert Type
                        <span className={sortArrowClass(batterySort.key, 'alertType')}>
                          {batterySortArrowChar('alertType')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setBatterySort((prev) => toggleSort(prev, 'vHrDrop'))}
                      >
                        Value
                        <span className={sortArrowClass(batterySort.key, 'vHrDrop')}>
                          {batterySortArrowChar('vHrDrop')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setBatterySort((prev) => toggleSort(prev, 'message'))}
                      >
                        Message
                        <span className={sortArrowClass(batterySort.key, 'message')}>
                          {batterySortArrowChar('message')}
                        </span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {batteryPagination.rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{batteryPagination.startDisplay + index}</td>
                      <td className="battery-dashboard-cell-asset-name">{row.assetName}</td>
                      <td>{row.dateTime}</td>
                      <td className="battery-dashboard-cell-alert-type">{row.alertType}</td>
                      <td className="battery-dashboard-cell-wrap">{row.vHrDrop}</td>
                      <td className="battery-dashboard-cell-wrap">{row.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="battery-dashboard-table-pagination">
                <span>{`${batteryPagination.page}/${batteryPagination.totalPages}`}</span>
                <div className="battery-dashboard-pagination-arrows">
                  <button
                    type="button"
                    onClick={() => setBatteryPage((prev) => Math.max(prev - 1, 1))}
                    disabled={batteryPagination.page <= 1}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setBatteryPage((prev) => Math.min(prev + 1, batteryPagination.totalPages))}
                    disabled={batteryPagination.page >= batteryPagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom-Left Table: Unplug & Security */}
          <div className="battery-dashboard-table-card">
            <h3 className="battery-dashboard-table-card-title battery-dashboard-table-header-purple">Unplug & Security</h3>
            <div className="battery-dashboard-table-container">
              <table className="battery-dashboard-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setUnplugSort((prev) => toggleSort(prev, 'assetName'))}
                      >
                        Asset Name
                        <span className={sortArrowClass(unplugSort.key, 'assetName')}>
                          {unplugSortArrowChar('assetName')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setUnplugSort((prev) => toggleSort(prev, 'dateTime'))}
                      >
                        Date &amp; Time
                        <span className={sortArrowClass(unplugSort.key, 'dateTime')}>
                          {unplugSortArrowChar('dateTime')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setUnplugSort((prev) => toggleSort(prev, 'status'))}
                      >
                        Status
                        <span className={sortArrowClass(unplugSort.key, 'status')}>
                          {unplugSortArrowChar('status')}
                        </span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {unplugPagination.rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{unplugPagination.startDisplay + index}</td>
                      <td className="battery-dashboard-cell-asset-name">{row.assetName}</td>
                      <td>{row.dateTime}</td>
                      <td className="battery-dashboard-cell-wrap">{row.status}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="battery-dashboard-table-pagination">
                <span>{`${unplugPagination.page}/${unplugPagination.totalPages}`}</span>
                <div className="battery-dashboard-pagination-arrows">
                  <button
                    type="button"
                    onClick={() => setUnplugPage((prev) => Math.max(prev - 1, 1))}
                    disabled={unplugPagination.page <= 1}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setUnplugPage((prev) => Math.min(prev + 1, unplugPagination.totalPages))}
                    disabled={unplugPagination.page >= unplugPagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Bottom-Right Table: Signal & Device Integrity */}
          <div className="battery-dashboard-table-card">
            <h3 className="battery-dashboard-table-card-title battery-dashboard-table-header-green">Signal & Device integrity</h3>
            <div className="battery-dashboard-table-container">
              <table className="battery-dashboard-table">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setSignalSort((prev) => toggleSort(prev, 'assetName'))}
                      >
                        Asset Name
                        <span className={sortArrowClass(signalSort.key, 'assetName')}>
                          {signalSortArrowChar('assetName')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setSignalSort((prev) => toggleSort(prev, 'dateTime'))}
                      >
                        Date &amp; Time
                        <span className={sortArrowClass(signalSort.key, 'dateTime')}>
                          {signalSortArrowChar('dateTime')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setSignalSort((prev) => toggleSort(prev, 'alertType'))}
                      >
                        Alert Type
                        <span className={sortArrowClass(signalSort.key, 'alertType')}>
                          {signalSortArrowChar('alertType')}
                        </span>
                      </button>
                    </th>
                    <th>
                      <button
                        type="button"
                        className="battery-dashboard-sortable-header"
                        onClick={() => setSignalSort((prev) => toggleSort(prev, 'message'))}
                      >
                        Message
                        <span className={sortArrowClass(signalSort.key, 'message')}>
                          {signalSortArrowChar('message')}
                        </span>
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {signalPagination.rows.map((row, index) => (
                    <tr key={row.id}>
                      <td>{signalPagination.startDisplay + index}</td>
                      <td className="battery-dashboard-cell-asset-name">{row.assetName}</td>
                      <td>{row.dateTime}</td>
                      <td className="battery-dashboard-cell-alert-type">{row.alertType}</td>
                      <td className="battery-dashboard-cell-wrap">{row.message}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="battery-dashboard-table-pagination">
                <span>{`${signalPagination.page}/${signalPagination.totalPages}`}</span>
                <div className="battery-dashboard-pagination-arrows">
                  <button
                    type="button"
                    onClick={() => setSignalPage((prev) => Math.max(prev - 1, 1))}
                    disabled={signalPagination.page <= 1}
                  >
                    Back
                  </button>
                  <button
                    type="button"
                    onClick={() => setSignalPage((prev) => Math.min(prev + 1, signalPagination.totalPages))}
                    disabled={signalPagination.page >= signalPagination.totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

export default BatteryDashboard;
